import React, {  useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import { ExpandMore } from '@mui/icons-material';

import useErrorManager from "../../../hooks/useErrorManager";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

import TextLinkButton from "../../Layout/TextLinkButton"
import GenericActionButton from '../../Layout/GenericActionButton';
import GenericDetailsTable from '../../Layout/Table/GenericDetailsTable';
import { manufacturersCode } from '../../../constants/manufacturersCode';

import { useNotification } from '../../../contexts/NotificationContext';

function GwMetersSection({ gatewayId }) {

    const navigate = useNavigate();
    const [meterScan, setMeterScan] = useState(null);
    
    const [loading, setLoading] = useState(true);

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();

    const axiosInstance = useAxiosPrivate();

    const scanTableColumns = [
        { field: 'manufacturerId', headerName: 'Manufacturer ID', flex: 1, renderCell: (params) => manufacturersCode[params.row.manufacturerId] || params.row.manufacturerId },
        { field: 'identificationNr', headerName: 'Device ID', flex: 1, renderCell: (params) => (renderSecAddr(params.row.identificationNr)) },
        { field: 'fabricationNr', headerName: 'Fabrication ID', flex: 1, renderCell: (params) => (renderSecAddr(params.row.fabricationNr)) },
        { field: 'deviceVersion', headerName: 'Version', flex: 1 },
        { field: 'deviceType', headerName: 'Device Type', flex: 1 },
        { field: 'signalQuality', headerName: 'Signal rating', flex: 1 },
        { field: 'siemeca', headerName: 'Reception level', flex: 1 }
    ];
      
     const detailsView = [
        { key: 'timeCompleted', label: 'Time of Scan', render: (item) => formatDateTime(item.action?.timeCompleted) },
        { key: 'duration', label: 'Scan duration',  render: (item) => Math.floor(item.action?.taskParams?.duration/60)  || "Undefined" },
        { key: 'foundMeters', label: 'Number of found Meters', render: (item) => item.meters?.length }
    ];

    useEffect(() => {
        const fetchMeterScanDetails = async () => {
            try {
                const response = await axiosInstance.get(`/meters/scan/${gatewayId}/latest`);
                setMeterScan(response.data);
                clearError('fetchMeterScan');
            } catch (error) {
                setError('fetchMeterScan', error.message);
                showNotification('Failed to fetch Meters Scan','error')
            } finally {
                setLoading(false); // Stop loading
            }
        };
        
        fetchMeterScanDetails();
    }, [gatewayId]);

    const renderSecAddr = (hexSecAddr) => {
        return hexSecAddr.toString(16);
    };
    
    const formatDateTime = (isoString) => {
        const date = new Date(isoString);
    
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
    
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
    
        return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    };

    const exportMetersData = () => {
        if (!meterScan || !meterScan.meters) {
            showNotification('No data to export', 'warning');
            return;
        }

        const columns = [
            { field: 'number', title: 'Number' }, // Added auto-incrementing column
            ...scanTableColumns.map(col => ({ field: col.field, title: col.headerName }))
        ];
        const data = meterScan.meters.map((row, index) => ({
            number: index + 1, // Auto-incrementing number
            manufacturerId: manufacturersCode[row.manufacturerId] || row.manufacturerId,
            identificationNr: renderSecAddr(row.identificationNr),
            fabricationNr: renderSecAddr(row.fabricationNr),
            deviceVersion: row.deviceVersion,
            deviceType: row.deviceType,
        }));

        exportPlt(columns, data, `MeterScan_${gatewayId}_${meterScan.action.timeCompleted}`);
    };

    const exportPlt = (columns, data, fileName, separator = '\t') => {
        const firstLine = "[PLANT]";
        const header = columns.map(col => col.title).join(separator);
        const rows = data.map(row => columns.map(col => row[col.field] || "").join(separator));
        const content = [firstLine, header, ...rows].join('\n');

        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName + '.plt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    const updateGatewayMetersList = async () => {

        if (!window.confirm("Are you sure you want to update the gateway meters list using this gateway scan?")) {
            return; // Cancel the update if the user doesn't confirm
        }

        if (!meterScan || !meterScan.meters) {
            showNotification('No data to update', 'warning');
            return;
        }
    
        try {
            // Prepare columns and data
            const formattedEntries = meterScan.meters.map((row, index) => ({
                number: index + 1, // Auto-incrementing number
                manufacturerId: manufacturersCode[row.manufacturerId] || row.manufacturerId,
                identificationNr: renderSecAddr(row.identificationNr),
                fabricationNr: renderSecAddr(row.fabricationNr),
                deviceVersion: row.deviceVersion,
                deviceType: row.deviceType,
            }));
    
            // Generate filename with timestamp
            const filename = `Meter Scan ${formatDateTime(meterScan.action.timeCompleted)}`;
    
            // Prepare the payload
            const payload = {
                filename: filename,
                entries: formattedEntries
            };
    
            // Send update request
            await axiosInstance.post(`/meters/list/${gatewayId}/json`, payload);
            showNotification('Meters list updated successfully', 'success');
    
        } catch (error) {
            setError('updateMetersList', error.message);
            showNotification('Failed to update meters list: ' + error.message, 'error');
        }
    };

    return (
        <>
            <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h7">METERS SCAN INFORMATIONS</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {loading ? ( 
                        // Show a loading spinner while data is being fetched
                        <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box>
                            {/* Shared Action Buttons */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
                                <Typography variant="h8">
                                    { meterScan?.meters?.length > 0 ? 'Latest scan informations:' : 'No scan data available'}
                                </Typography>
                                <GenericActionButton
                                    actions={[
                                        { label: 'Manage Meters List', onClick: () => navigate(`/gateways/${gatewayId}/meters`) },
                                        { label: 'Meters Scan', onClick: () => navigate(`/gateways/${gatewayId}/scan/meters`) },
                                        { label: 'Meters Readout', onClick: () => navigate(`/gateways/${gatewayId}/readouts`) },
                                        { label: 'Export Scan List', onClick: exportMetersData },
                                        { label: 'Set as Gateway List', onClick: updateGatewayMetersList }
                                    ]}
                                />
                            </Box>

                            { meterScan?.meters?.length > 0 ? (
                                <>
                                    <GenericDetailsTable
                                        detailsValue={meterScan}
                                        detailsList={detailsView}
                                    />
                                    <DataGrid 
                                        rows={meterScan.meters} 
                                        columns={scanTableColumns} 
                                        initialState={{
                                            pagination: {
                                                paginationModel: { pageSize: 5, page: 0 },
                                            },
                                        }}
                                        pageSizeOptions={[5, 10, 25]}
                                        getRowId={(row) => row.fabricationNr}
                                    />
                                </>
                            ) : null}
                        </Box>
                    )}
                </AccordionDetails>

            </Accordion>
        </>     
    );
}

export default GwMetersSection;
